import { useCallback, useEffect, useState } from 'react';
import { api, type AuthSession, type PresetRow } from '../net/api.js';
import type { Preset } from '../settings.js';

/**
 * Presets, wherever they belong.
 *
 * A signed-in teacher's presets live on the server, so the set they built at
 * home is there on the classroom machine. Everyone else keeps them in this
 * browser — practice mode must not require an account.
 */

interface Stored { overrides?: Preset['overrides']; theme?: Preset['theme'] }

const fromRow = (r: PresetRow): Preset => {
  const stored = (r.overrides ?? {}) as Stored;
  return {
    id: r.id,
    name: r.name,
    grade: r.grade as Preset['grade'],
    level: r.level,
    overrides: stored.overrides ?? {},
    theme: stored.theme ?? 'auto',
  };
};

export interface PresetStore {
  list: Preset[];
  /** True once the server has confirmed a signed-in teacher. */
  synced: boolean;
  save: (preset: Preset) => void;
  remove: (id: string) => void;
}

export function usePresets(local: Preset[], setLocal: (next: Preset[]) => void): PresetStore {
  const [server, setServer] = useState<Preset[] | null>(null);

  useEffect(() => {
    let live = true;
    // Ask who we are first: fetching presets while signed out would 401, and a
    // red console error on a child's practice screen is noise, not information.
    api.get<AuthSession>('/auth/session')
      .then((s) => (s.signedIn ? api.get<PresetRow[]>('/classes/presets/all') : null))
      .then((rows) => { if (live) setServer(rows ? rows.map(fromRow) : null); })
      .catch(() => { if (live) setServer(null); });
    return () => { live = false; };
  }, []);

  const save = useCallback((preset: Preset) => {
    if (server === null) { setLocal([...local, preset]); return; }
    void api.post<PresetRow>('/classes/presets/all', {
      name: preset.name,
      grade: preset.grade,
      level: preset.level,
      overrides: { overrides: preset.overrides, theme: preset.theme },
    }).then((row) => setServer((prev) => [...(prev ?? []), fromRow(row)]))
      .catch(() => setLocal([...local, preset]));
  }, [local, server, setLocal]);

  const remove = useCallback((id: string) => {
    if (server === null) { setLocal(local.filter((p) => p.id !== id)); return; }
    void api.del(`/classes/presets/${id}`)
      .then(() => setServer((prev) => (prev ?? []).filter((p) => p.id !== id)))
      .catch(() => undefined);
  }, [local, server, setLocal]);

  return { list: server ?? local, synced: server !== null, save, remove };
}
